import { BannerContext } from 'components/Routes/BannerContext';
import CarnivoreProvider from 'components/editor/CarnivoreProvider';
import ECSWorldProvider from 'components/editor/ECSWorldProvider';
import MarkupKitProvider from 'components/editor/MarkupKitProvider';
import { isIpad } from 'constants/browserDetection';
import CarnivoreContext from 'contexts/CarnivoreContext';
import ECSEditorContext from 'contexts/ECSEditorContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import CarnivoreCtx from 'editor/components/CarnivoreCtx';
import CarnivoreDoc from 'editor/components/CarnivoreDoc';
import DOMOverlay from 'editor/components/DOMOverlay';
import EditorMode from 'editor/components/EditorMode';
import Tool, { EditorTools } from 'editor/components/Tool';
import { addEditorSystems } from 'editor/systems';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type Props = PropsWithChildren<{
  containerId: string;
  // heightStyle: string; // TODO - maybe include styles override
  // withMarkups: boolean; // TBD - is this necessary to support toolbar?
  isModal: boolean; // TBD -  - maybe replace with styles override
  isScrollable?: boolean; // TBD -  maybe replace with styles override
}>;

const ECSViewerEditor: FC<Props> = (props: Props) => {
  const { children, containerId, isModal, isScrollable } = props;
  const carnivore = useContext(CarnivoreContext);
  const world = useContext(ECSWorldContext);
  const { height: bannerHeight } = useContext(BannerContext);

  const [doc, setDoc] = useState<CarnivoreDoc | null>(null);
  const [carnCtx, setCarnCtx] = useState<CarnivoreCtx | null>(null);
  const [domOverlay, setDOMOverlay] = useState<DOMOverlay | null>(null);
  const [tool, setTool] = useState<Tool<EditorTools> | null>(null);
  const [mode, setMode] = useState<EditorMode | null>(null);
  const rafId = useRef<number>(0);
  const [, forceUpdate] = useState(Math.random());

  useEffect(() => {
    addEditorSystems(world);

    function renderTick() {
      world.systems.run();

      rafId.current = window.requestAnimationFrame(renderTick);
    }

    renderTick();

    // @ts-ignore
    window.world = world;

    return () => {
      world.entities.forEach((entity) => entity.destroy());
      world.systems.run();
      window.cancelAnimationFrame(rafId.current);
    };
  }, []);

  useEffect(() => {
    const _carnCtx = new carnivore.Carnivore();

    const _doc = _carnCtx.createDocument({
      id: 'doc-1',
      containerId,
      backgroundColor: [0.7647058824, 0.7725490196, 0.7882352941, 1.0], // rgb(195, 197, 201)
      minZoom: 0.5,
    });

    // TODO: Wrap all these component in a single type, probably ECSEditorContextContent.
    const _docComp = new CarnivoreDoc(_doc);
    const _carnCtxComp = new CarnivoreCtx(_carnCtx);
    const _domOverlayComp = new DOMOverlay('dom-overlay', 1);
    const _toolComp = new Tool(EditorTools.Pointer);
    const _modeComp = new EditorMode();

    // Force tool change to rerender
    _toolComp.on('toolchange', () => {
      forceUpdate(Math.random());
    });

    setDoc(_docComp);
    setCarnCtx(_carnCtxComp);
    setDOMOverlay(_domOverlayComp);
    setTool(_toolComp);
    setMode(_modeComp);

    return () => {
      _carnCtx.destroy();
    };
  }, []);

  // TODO where do these come from? is explicit height even required here?
  // Probably replace/update with a styles override prop
  const headerHeight = '48px';
  const footerHeight = isModal ? '52px' : '48px';
  const inset = isModal ? '3rem' : '0rem'; // double for top and bottom @ 1.5em each
  const scrollmargin = isScrollable ? '15px' : '0px';
  const iPadHeightAdjustment = isIpad ? '96vh' : '100vh';
  const heightStyle = `calc(${iPadHeightAdjustment} - ${headerHeight} - ${bannerHeight}px - ${footerHeight} - ${inset} - ${scrollmargin})`;
  return (
    <div className="pos-r">
      <div
        id={containerId}
        className="bgd-gray-2"
        style={{ height: heightStyle }}
        onContextMenu={(e) => e.preventDefault()}
      />
      {doc && carnCtx && domOverlay && tool && mode ? (
        <ECSEditorContext.Provider
          value={{ doc, carnCtx, domOverlay, tool, mode }}
        >
          {children}
        </ECSEditorContext.Provider>
      ) : null}
    </div>
  );
};

const ECSEditorWrapper: FC<Props> = (props: Props) => {
  return (
    <MarkupKitProvider>
      <CarnivoreProvider>
        <ECSWorldProvider>
          <ECSViewerEditor {...props} />
        </ECSWorldProvider>
      </CarnivoreProvider>
    </MarkupKitProvider>
  );
};

export default ECSEditorWrapper;
